import { useEffect, useState } from 'react';
import { ListChecks, WifiOff } from 'lucide-react';
import { useJobCenterOptional } from '../contexts/JobCenterContext';
import { useJobCenterToasts } from '../hooks/useJobCenterToasts';
import { JobCenterDrawer } from './jobs/JobCenterDrawer';
import { useScopedJobs } from './jobs/useScopedJobs';
import { cn } from './ui/utils';

/**
 * Global Job Center entry point — a floating badge button mounted once at
 * the app root (main.tsx), so it's reachable from every route (/chat,
 * /workspace, /admin/*) without threading state through three separate page
 * shells. This is the "global, real-time Job Center" surface: long-running
 * operations are never shown as a page-specific loading screen, they show up
 * here regardless of what the user navigates to next.
 */
export function JobCenterLauncher() {
  const [open, setOpen] = useState(false);
  // The live SSE feed connects a moment AFTER the initial hydrate resolves. On a
  // cold first load that gap can be a few seconds, during which the launcher used
  // to flash a "reconnecting" badge and alarm the clinician into refreshing. Give
  // the first connect a short grace before surfacing a degraded feed; a genuine
  // outage still shows once the grace elapses, and any LATER drop shows at once.
  const [pastConnectGrace, setPastConnectGrace] = useState(false);
  const jobCenter = useJobCenterOptional();
  // The badge must count what the drawer under it LISTS. Reading the context's
  // fleet-wide counts put a "12" over a drawer showing 2 for the open patient
  // -- a number the reader cannot reconcile with anything on screen, which
  // makes them distrust both. Same scope, one source.
  const scoped = useScopedJobs();
  useJobCenterToasts();

  useEffect(() => {
    const t = window.setTimeout(() => setPastConnectGrace(true), 4000);
    return () => window.clearTimeout(t);
  }, []);

  if (!jobCenter) return null;

  const { offline, connected, hydrating } = jobCenter;
  const { activeCount, needsReviewCount } = scoped;
  const degraded = !offline && !connected && !hydrating && pastConnectGrace;
  const badgeCount = needsReviewCount > 0 ? needsReviewCount : activeCount;
  const isFailedBadge = needsReviewCount > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // Sit ABOVE the chat composer rather than on top of it. The launcher
        // used to cover the composer's Send button outright -- measured at
        // every viewport, elementFromPoint at Send's centre resolved to this
        // button, so clicking Send opened the Job Center instead of sending.
        // A secondary affordance must never eat the page's primary action.
        // Composer publishes its measured height (it grows 48px->200px as you
        // type), and falls back to 0 on routes with no composer.
        style={{ bottom: 'calc(var(--composer-dock-height, 0px) + 1.25rem)' }}
        className={cn(
          'fixed right-5 z-30 flex h-11 w-11 items-center justify-center rounded-full border shadow-lg transition-colors',
          isFailedBadge
            ? 'border-red-500/40 bg-red-600 text-white hover:bg-red-500'
            : activeCount > 0
              ? 'border-primary/40 bg-primary text-primary-foreground hover:bg-primary/90'
              : 'border-sidebar-border bg-sidebar text-sidebar-foreground hover:bg-sidebar-accent',
        )}
        aria-label={
          offline
            ? 'Open Job Center — offline, jobs keep running on the server'
            : degraded
              ? 'Open Job Center — live feed reconnecting'
              : 'Open Job Center'
        }
        title={
          offline
            ? 'Job Center — offline (jobs keep running on the server)'
            : degraded
              ? 'Job Center — reconnecting'
              : 'Job Center'
        }
      >
        <ListChecks className="h-5 w-5" />
        {/* Connection state on the launcher itself: a clinician should know the
            feed is offline/reconnecting BEFORE opening the drawer. Icon + label
            (in the accessible name), never colour alone. */}
        {offline || degraded ? (
          <span
            className="absolute -bottom-1 -left-1 flex h-4 w-4 items-center justify-center rounded-full border border-current bg-background"
            aria-hidden
          >
            <WifiOff className="h-2.5 w-2.5 text-band-moderate-text" />
          </span>
        ) : null}
        {badgeCount > 0 ? (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full border border-current bg-background px-1 text-[9px] font-medium text-foreground">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        ) : null}
      </button>
      <JobCenterDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}
